import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { B, radius, space, font, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Input, Textarea, Select, Icon } from '../ui';
import { DuplicateTaskDialog } from './DuplicateTaskDialog';
import { SaveFileToDealModal } from './SaveFileToDealModal';
import { saveXlsxResult, saveDocumentResult } from '../../utils/exportDownload';
import { formatInstant } from '../../utils/format';
import { FixedScopeProjectPicker } from './DealScopePicker';

// AssistantDraftCard — a proposal Harvi made, for the user to edit and save.
//
// THE CARD IS THE WHOLE SAFETY MODEL, so it is a first-class control rather
// than a confirmation dialog. Harvi cannot write to Harvus: a propose_* tool
// records a draft and nothing else, and what turns it into a row is the person
// reading this card pressing Save — which calls the SAME endpoint the app's own
// forms call, with their own token. So the note is authored by them, the
// ordinary validation and notifications apply, and a prompt injection sitting
// in a document summary can at worst produce a card somebody declines.
//
// Which means the fields have to be genuinely editable, not a preview. The
// model gets the assignee wrong, or drafts a note in the wrong voice, and the
// point of the design is that this is visible and fixable before it lands.
//
// IT NEVER SAYS "SAVED" ON ITS OWN AUTHORITY. Status comes from the server
// (GET /api/assistant/drafts), so a reloaded page shows a saved draft as saved
// and does not offer to save it twice.
//
// THE NEAR-DUPLICATE GATE STAYS ON, and routes through the same
// DuplicateTaskDialog every other web create path uses. This is exactly the
// case that gate exists for — a model asked twice will propose twice — so
// passing confirmDuplicate up front (as the batch paths do, having pre-reviewed
// every item) would be the one place it must not be bypassed.

const KIND_COPY = {
  note: {
    title: 'Draft note',
    blurb: 'Harvi drafted this for you to review. Nothing is saved until you press Save.',
    save: 'Save note',
  },
  task: {
    title: 'Draft task',
    blurb: 'Harvi drafted this for you to review. Nothing is saved until you press Save.',
    save: 'Create task',
  },
  taskCompletion: {
    title: 'Mark a task complete',
    blurb: 'Harvi cannot complete a task itself. Confirm below and it will be marked complete by you.',
    save: 'Mark complete',
  },
  emailReply: {
    title: 'Draft reply',
    // Said as flatly as possible, because it is the one thing a reader could
    // get wrong in a way that matters: this button does NOT send.
    blurb: 'Nothing is sent. Save puts this in your Outlook Drafts folder, addressed and quoted — '
         + 'you press Send there. Harvi has no permission to send mail at all.',
    save: 'Put in my Drafts',
  },
  calendarEvent: {
    title: 'Draft event',
    blurb: 'Nothing is on your calendar until you press Save. Any attendees are invited at that point.',
    save: 'Add to my calendar',
  },
  mailFiling: {
    title: 'Filing plan',
    // THE POINT OF THIS CARD IS THAT NOTHING HAS MOVED YET, so that is the
    // first thing it says. Filing is the one proposal here that changes where
    // things a person already has are kept, and "I tidied your inbox" read as a
    // fait accompli is the reaction this copy exists to prevent.
    blurb: 'Nothing has moved. Check where each message is going — change any destination, '
         + 'untick anything you want left alone — and only then file them.',
    save: 'File these messages',
  },
  mailAttachment: {
    title: 'Save attachment to deal',
    // Unlike emailReply/calendarEvent, what this commits is a HARVUS write —
    // a document row, not a Graph one — so the blurb says where it lands
    // rather than reassuring about what it does not send.
    blurb: 'Nothing is saved until you press Save. The file is fetched from the email and added as a '
         + 'document on the deal below, where it will be read and filed automatically like any other upload.',
    save: 'Save to deal',
  },
  workbook: {
    title: 'Draft workbook',
    // It writes nothing to Harvus, so the blurb is about trust rather than
    // about what gets saved: typed-in figures were drafted by a model, and the
    // formulas are Excel's to calculate.
    blurb: 'Harvi drafted this spreadsheet. Formulas calculate when you open it; any figure typed '
         + 'into a cell was drafted by Harvi from the sources listed — check them. Download it, or save it to a deal\'s Documents.',
    save: 'Download .xlsx',
  },
  document: {
    title: 'Draft document',
    // One body, two files: Word to edit, PDF to send. Same trust note as a
    // workbook — every figure in it was written by a model.
    blurb: 'Harvi drafted this document. Download it as Word to edit or as PDF to send; every figure '
         + 'in it was written by Harvi from the sources listed — check them. Download it, or save it to a deal\'s Documents.',
    save: 'Download Word',
  },
};

// A preview is a glance at the structure, not the file — enough rows to see
// what each sheet is for.
const PREVIEW_ROWS = 15;
const PREVIEW_COLS = 8;
const WORKBOOK_TIMEOUT_MS = 60000;

// How a normalized cell reads in the preview: formulas shown as formulas,
// because the card never pretends to know what they evaluate to.
const cellText = (c) => {
  if (c === null || c === undefined) return '';
  if (typeof c === 'number' || typeof c === 'boolean') return String(c);
  if (c.f) return `=${c.f}`;
  return String(c.v ?? '');
};

// One glyph per kind, so a card is identifiable before a word of it is read.
const KIND_ICONS = {
  note: 'note', task: 'tasks', taskCompletion: 'check',
  emailReply: 'emailInbox', calendarEvent: 'calendar', mailFiling: 'folder',
  mailAttachment: 'documents', workbook: 'download', document: 'file',
};

// A small, local formatter — the same shape EntityDocumentsPanel.jsx already
// carries its own copy of, rather than one shared utility: a 4-line
// byte-to-KB/MB conversion is not worth a module, and importing from the DAM's
// damApi.js would reach into a module documented self-contained/removable.
const fmtBytes = (n) => {
  if (n == null) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_COPY = {
  saved:     { label: 'Saved', color: B.green },
  discarded: { label: 'Discarded', color: B.textLt },
  expired:   { label: 'Expired — ask Harvi to draft it again', color: B.textLt },
};

// A PROPOSAL IS A CARD, NOT A TINTED BOX. It carries editable fields and the
// one button in the panel that writes to Harvus, so it has to read as a piece
// of UI you act on rather than as a quoted block inside the answer — hence a
// white surface lifted off the transcript, a titled header strip, and a footer
// rule under the actions.
const wrap = {
  border: `1px solid ${B.divider}`,
  borderRadius: radius.xxxl,
  background: B.surface,
  boxShadow: B.shadowSm,
  marginTop: 12,
  overflow: 'hidden',
};

const cardBody = {
  padding: `${space.md}px ${space.lg}px ${space.lg}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: space.md,
};

export const AssistantDraftCard = ({ draft, assignees = [], onChanged, onApplied, defaultTarget = null }) => {
  const payload = draft.payload || {};
  // "Save to deal" for a workbook/document: the modal is mounted only when
  // opened, so the deal list is fetched only then.
  const [savingToDeal, setSavingToDeal] = useState(false);
  const [savedToDeal, setSavedToDeal] = useState(null);
  const copy = KIND_COPY[draft.kind] || KIND_COPY.note;

  // Edits are staged locally, so closing the thread without saving really does
  // discard them — the same rule DashboardCustomizeModal follows.
  const [body, setBody]     = useState(payload.body || '');
  const [title, setTitle]   = useState(payload.title || '');
  const [desc, setDesc]     = useState(payload.description || '');
  const [due, setDue]       = useState(payload.dueDate || '');
  const [assignee, setAssignee] = useState(payload.assignedToId || '');
  const [note, setNote]     = useState(payload.completionNote || '');
  // ORTHOGONAL TAG (see ProjectPicker.jsx) — editable on note/task drafts,
  // seeded from whatever Harvi resolved (already fail-soft-checked against the
  // target's deal server-side, so a bad id never reaches here as a value).
  const [projectId, setProjectId] = useState(payload.projectId || '');
  // The personal kinds. `start`/`end` are LOCAL (no zone suffix) — see the
  // normalizer: a bare time plus an explicit timeZone is what Graph wants, and
  // sending an instant with a zone beside it is two conflicting answers.
  const [subject, setSubject] = useState(payload.subject || '');
  const [start, setStart]     = useState(payload.start || '');
  const [end, setEnd]         = useState(payload.end || '');
  const [tz, setTz]           = useState(payload.timeZone || '');
  const [where, setWhere]     = useState(payload.location || '');
  const [attendees, setAttendees] = useState((payload.attendees || []).join(', '));
  // THE FILING PLAN IS A LIST, so its state is a list — one row per message,
  // each with its own destination and its own keep/drop. Staged locally like
  // every other field on this card, so closing the thread really does discard
  // the edits.
  const [rows, setRows] = useState(() => (payload.items || []).map(it => ({ ...it, keep: true })));
  const [applied, setApplied] = useState(null);   // { message, webLink, moved, skipped, foldersCreated }
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);
  const [dupes, setDupes]   = useState(null);   // the 409's similarTasks
  const [sheetIdx, setSheetIdx] = useState(0);

  const kept = rows.filter(r => r.keep);

  // WHICH DESTINATIONS DO NOT EXIST YET — the second thing a person has to
  // check, and the one that changes their mailbox rather than just moving mail
  // about in it. The server computed this against the live tree when it built
  // the plan (`createFolders`, which includes any missing PARENT folder); a
  // destination the person then types by hand is added to it here, so the count
  // can only ever go up as they edit. It is a hint, not the authority — the
  // apply reports what it actually created, since somebody may have made one of
  // these by hand in the meantime.
  const newFolders = useMemo(() => {
    const proposed = payload.createFolders || [];
    const have = new Set((payload.existingFolders || []).map(f => String(f).toLowerCase()));
    const out = [...proposed];
    const known = new Set(proposed.map(f => String(f).toLowerCase()));
    for (const r of kept) {
      const key = String(r.folder || '').toLowerCase();
      if (!key || have.has(key) || known.has(key)) continue;
      known.add(key);
      out.push(r.folder);
    }
    return out;
  }, [payload.createFolders, payload.existingFolders, kept]);

  const settled = draft.status !== 'open';
  const isFileKind = draft.kind === 'workbook' || draft.kind === 'document';
  // What must be filled in before Save is worth offering, per kind. Stated once
  // rather than inline on the button, which is where a new kind gets forgotten
  // and ships with Save permanently enabled.
  const hasRequired = (
    draft.kind === 'note'          ? !!body.trim()
    : draft.kind === 'task'        ? !!title.trim()
    : draft.kind === 'emailReply'  ? !!body.trim()
    : draft.kind === 'calendarEvent' ? (!!subject.trim() && !!start)
    // Untick everything and there is nothing to file — the server refuses it
    // too, but there is no reason to make somebody press Save to find out.
    : draft.kind === 'mailFiling' ? (kept.length > 0 && kept.every(r => String(r.folder || '').trim()))
    : true
  );
  const stat = STATUS_COPY[draft.status];

  // A <select> whose value is not among its options renders EMPTY, and saving
  // from there would silently reassign the task. So the draft's own assignee is
  // always an option, even if they are no longer in the active user list.
  const assigneeOptions = (() => {
    const opts = assignees.map(u => ({ value: u.UserID, label: u.DisplayName || u.Email }));
    if (assignee && !opts.some(o => String(o.value).toLowerCase() === String(assignee).toLowerCase())) {
      opts.unshift({ value: assignee, label: 'Currently assigned' });
    }
    return opts;
  })();

  const save = async (confirmDuplicate = false, { format = 'docx' } = {}) => {
    setBusy(true);
    setErr(null);
    try {
      // Every branch posts to the endpoint the app's own forms post to. There is
      // deliberately no /assistant/drafts/{id}/save — a second write path would
      // need its own copy of the validation and the notification fan-out.
      if (draft.kind === 'note') {
        await api.post('notes', {
          dealId: payload.dealId || undefined,
          dealDetailId: payload.dealDetailId || undefined,
          taskId: payload.taskId || undefined,
          projectId: projectId || undefined,
          content: body,
          noteDate: payload.noteDate || undefined,
          assistantDraftId: draft.draftId,
        });
      } else if (draft.kind === 'task') {
        await api.post('tasks', {
          dealId: payload.dealId || undefined,
          dealDetailId: payload.dealDetailId || undefined,
          projectId: projectId || undefined,
          title,
          description: desc || undefined,
          dueDate: due || undefined,
          priority: payload.priority || undefined,
          status: payload.status || undefined,
          assigneeIds: assignee ? [assignee] : [],
          // The same draft id on the resubmit, so confirming a duplicate is
          // still ONE save — the id is the idempotency key.
          confirmDuplicate: confirmDuplicate || undefined,
          assistantDraftId: draft.draftId,
        });
      } else if (draft.kind === 'mailFiling') {
        // Only the rows still ticked, and only the two fields the person can
        // change. The server keeps the message set to what it proposed, so a
        // stale row cannot re-point the plan at other mail.
        const res = await api.post(`assistant/drafts/${draft.draftId}/apply`, {
          items: kept.map(r => ({ messageId: r.messageId, folder: r.folder })),
        });
        const result = {
          message: res?.message || null,
          moved: res?.moved,
          skipped: res?.skipped || [],
          foldersCreated: res?.foldersCreated || [],
        };
        setApplied(result);
        // The daily brief rebuilds its plan off this — it needs the outcome,
        // not just "something changed".
        onApplied?.(result);
      } else if (draft.kind === 'workbook') {
        // NOT A SAVE: the route builds the file from the stored draft and hands
        // back a link. Nothing is written to Harvus.
        const res = await api.post(`assistant/drafts/${draft.draftId}/workbook`, {},
          { timeout: WORKBOOK_TIMEOUT_MS });
        saveXlsxResult(res);
      } else if (draft.kind === 'document') {
        // Also not a save: the same stored body built as the format asked for.
        const res = await api.post(`assistant/drafts/${draft.draftId}/document`, { format },
          { timeout: WORKBOOK_TIMEOUT_MS });
        saveDocumentResult(res);
        // A logo deleted since the draft was made is left off, not a failure —
        // but the person is told, so the file is not taken to a meeting as-is.
        if (res?.logosMissing?.length) {
          setErr(`Downloaded without ${res.logosMissing.join(', ')} — that logo is no longer in the library.`);
        }
      } else if (draft.kind === 'mailAttachment') {
        // NOTHING TO SEND — the file, the message and the target are all
        // fixed at propose time (see normalizeAttachmentDraft; there is no
        // re-targeting control on this card), so an empty body is "apply
        // exactly as proposed", the same convention an untouched mailFiling
        // row already uses. It still goes through /apply rather than an
        // ordinary Harvus endpoint, because reaching the bytes needs this
        // person's own delegated Graph token — the same reason
        // emailReply/calendarEvent have no ordinary save route either, even
        // though what this one commits is a Harvus document, not a Graph
        // write.
        const res = await api.post(`assistant/drafts/${draft.draftId}/apply`, {});
        setApplied({ message: res?.message || null });
      } else if (draft.kind === 'emailReply' || draft.kind === 'calendarEvent') {
        // THE ONE PLACE THERE IS A SAVE ROUTE, and the reason is that there is
        // no app endpoint to reuse: Harvus has no mail or calendar API. It is
        // not a second write path — there is exactly one, Microsoft Graph with
        // this person's own delegated token, which cannot act as anybody else.
        const res = await api.post(`assistant/drafts/${draft.draftId}/apply`,
          draft.kind === 'emailReply'
            ? { body }
            : { subject, start, end, timeZone: tz, location: where,
                attendees: attendees.split(',').map(a => a.trim()).filter(Boolean) });
        // What it became is shown rather than assumed: "Save" on a reply must
        // never read as "Sent".
        setApplied({ message: res?.message || null, webLink: res?.webLink || null });
      } else {
        // Completion is two writes and the ORDER matters: the status change
        // first, so the draft records the task it completed, then the note.
        // Reversed, a failure between them would leave a note saying the work
        // is done on a task that is still open.
        await api.put(`tasks/${payload.taskId}/status`, {
          status: 'Complete',
          assistantDraftId: draft.draftId,
        });
        if (note.trim()) {
          await api.post('notes', {
            taskId: payload.taskId,
            content: note.trim(),
            assistantDraftId: draft.draftId,
          });
        }
      }
      setDupes(null);
      onChanged?.();
    } catch (e) {
      // A 409 carrying similarTasks is not a failure — it is the duplicate gate
      // asking. Note the body is camelCase already: the API client does NOT
      // camel-case error bodies, so these keys are read as-is.
      if (e?.status === 409 && Array.isArray(e?.body?.similarTasks)) {
        setDupes(e.body.similarTasks);
      } else {
        // Otherwise the API's own message, verbatim — it names the reason (an
        // expired draft, a refused assignee) far better than anything invented
        // here could.
        setErr(e?.message || 'Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`assistant/drafts/${draft.draftId}/discard`, {});
      onChanged?.();
    } catch (e) {
      setErr(e?.message || 'Could not discard that.');
    } finally {
      setBusy(false);
    }
  };

  if (settled) {
    return (
      <>
      <div style={{
        ...wrap, background: B.surfaceMuted, boxShadow: 'none',
        padding: `${space.sm + 2}px ${space.md}px`, display: 'flex', gap: space.sm,
        alignItems: 'center',
      }}>
        <span style={{ display: 'flex', color: stat?.color || B.textLt, flexShrink: 0 }}>
          <Icon name={draft.status === 'saved' ? 'success' : 'close'} size={14} />
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd }}>{copy.title}</span>
          <span style={{
            display: 'block', fontSize: font.xs, color: B.textLt,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {payload.title || payload.taskTitle || payload.fileName
              || (draft.kind === 'mailFiling'
                    ? `${(payload.items || []).length} message${(payload.items || []).length === 1 ? '' : 's'}`
                    : null)
              || (payload.body || '').slice(0, 200)}
          </span>
        </span>
        <span style={{
          flexShrink: 0, fontSize: font.xs, fontWeight: weight.semibold,
          color: stat?.color || B.textLt, background: `${stat?.color || B.textLt}14`,
          border: `1px solid ${stat?.color || B.textLt}2e`,
          borderRadius: radius.pill, padding: '2px 9px',
        }}>{isFileKind && draft.status === 'saved' ? 'Downloaded' : (stat?.label || draft.status)}</span>
        {/* A file is not single-use: re-downloading builds it again and
            writes nothing, so a downloaded workbook or document keeps its button. */}
        {draft.kind === 'workbook' && draft.status === 'saved' && (
          <Btn size="sm" variant="secondary" onClick={() => save()} disabled={busy}>
            {busy ? 'Building…' : 'Download again'}
          </Btn>
        )}
        {draft.kind === 'document' && draft.status === 'saved' && (
          <>
            <Btn size="sm" variant="secondary" onClick={() => save(false, { format: 'docx' })} disabled={busy}>
              {busy ? 'Building…' : 'Word'}
            </Btn>
            <Btn size="sm" variant="secondary" onClick={() => save(false, { format: 'pdf' })} disabled={busy}>PDF</Btn>
          </>
        )}
        {isFileKind && draft.status === 'saved' && (
          <Btn size="sm" variant="secondary" onClick={() => setSavingToDeal(true)} disabled={busy}>Save to deal</Btn>
        )}
      </div>
      {savedToDeal && (
        <div style={{ fontSize: font.xs, color: B.green, padding: `${space.xs}px ${space.md}px 0` }}>{savedToDeal}</div>
      )}
      {savingToDeal && (
        <SaveFileToDealModal
          draft={draft}
          defaultTarget={defaultTarget}
          onClose={() => setSavingToDeal(false)}
          onSaved={(res) => {
            const missing = res?.logosMissing?.length ? ` Saved without ${res.logosMissing.join(', ')} (no longer in the library).` : '';
            setSavedToDeal(`${res?.message || 'Saved to the deal.'}${missing}`);
            onChanged?.();
          }}
        />
      )}
      {err && isFileKind && (
        <div style={{ fontSize: font.xs, color: B.red, padding: `${space.xs}px ${space.md}px 0` }}>{err}</div>
      )}
      {/* WHAT DID NOT MOVE OUTLIVES THE CARD. Saving refetches the draft, which
          settles it and collapses this to one line — and for a filing plan that
          would take the list of messages STILL IN THE INBOX off the screen the
          moment it became relevant. The message and the leftovers are kept
          beneath the collapsed row for the rest of the session. */}
      {applied && (applied.message || (applied.skipped || []).length > 0) && (
        <div style={{
          fontSize: font.xs, color: B.textMd, padding: `${space.xs}px ${space.md}px 0`,
        }}>
          <div>{applied.message}</div>
          {(applied.skipped || []).length > 0 && (
            <div style={{ marginTop: 3, color: B.amber }}>
              Left in your Inbox:
              <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                {applied.skipped.map((sk, i) => (
                  <li key={i}><strong>{sk.subject}</strong>{sk.reason ? ` — ${sk.reason}` : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      </>
    );
  }

  return (
    <div style={wrap}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: space.md,
        padding: `${space.md}px ${space.lg}px`,
        background: B.tealLt, borderBottom: `1px solid ${B.divider}`,
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: radius.xl, flexShrink: 0,
          background: B.surface, color: B.navy, boxShadow: B.shadowXs,
        }}><Icon name={KIND_ICONS[draft.kind] || 'note'} size={15} /></span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.navyDk }}>{copy.title}</div>
          <div style={{ fontSize: font.xs, color: B.navyDk, opacity: 0.78, marginTop: 2, lineHeight: 1.5 }}>{copy.blurb}</div>
          {payload.targetLabel && (
            <div style={{ fontSize: font.xs, color: B.navyDk, opacity: 0.6, marginTop: 3 }}>On {payload.targetLabel}</div>
          )}
        </div>
      </div>

      <div style={cardBody}>
      {/* Soft failures the server chose to show rather than refuse over — an
          assignee it could not use, say. Fail-soft on a draft is right: the
          person sees it before it becomes a row. */}
      {(draft.warnings || []).map((w, i) => (
        <div key={i} style={{
          display: 'flex', gap: 7, alignItems: 'flex-start',
          fontSize: font.xs, color: B.amber, background: `${B.amber}12`,
          border: `1px solid ${B.amber}2e`, borderRadius: radius.lg, padding: '7px 10px',
        }}>
          <span style={{ display: 'flex', marginTop: 1 }}><Icon name="warning" size={13} /></span>{w}
        </div>
      ))}

      {draft.kind === 'note' && (
        <>
          <Textarea label="Note" value={body} onChange={setBody} rows={4} disabled={busy} />
          {payload.dealId && (
            <FixedScopeProjectPicker dealId={payload.dealId} dealDetailId={payload.dealDetailId} disabled={busy}
              value={projectId} onChange={setProjectId} />
          )}
        </>
      )}

      {draft.kind === 'task' && (
        <>
          <Input label="Title" value={title} onChange={setTitle} disabled={busy} />
          <Textarea label="Description" value={desc} onChange={setDesc} rows={3} disabled={busy} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px' }}>
              <Input label="Due" type="date" value={due} onChange={setDue} disabled={busy} />
            </div>
            {assignees.length > 0 && (
              <div style={{ flex: '1 1 180px' }}>
                <Select
                  label="Assigned to"
                  value={assignee}
                  onChange={setAssignee}
                  disabled={busy}
                  options={assigneeOptions}
                />
              </div>
            )}
          </div>
          {payload.dealId && (
            <FixedScopeProjectPicker dealId={payload.dealId} dealDetailId={payload.dealDetailId} disabled={busy}
              value={projectId} onChange={setProjectId} />
          )}
        </>
      )}

      {draft.kind === 'taskCompletion' && (
        <>
          <div style={{ fontSize: 13, color: B.text }}>{payload.taskTitle || 'This task'}</div>
          <Textarea label="What was done (optional)" value={note} onChange={setNote} rows={2} disabled={busy} />
        </>
      )}

      {draft.kind === 'emailReply' && (
        <>
          {/* Who and what is being replied to, from the server's own read of
              the message — a draft reply with no visible subject is one you
              have to open Outlook to understand. */}
          <div style={{ fontSize: 12, color: B.textMd }}>
            Re: <strong style={{ color: B.text }}>{payload.subject || '(no subject)'}</strong>
            {payload.to ? ` — to ${payload.to}` : ''}
            {payload.replyAll ? ' (reply to everyone)' : ''}
          </div>
          <Textarea label="Reply" value={body} onChange={setBody} rows={6} disabled={busy} />
        </>
      )}

      {draft.kind === 'calendarEvent' && (
        <>
          <Input label="Subject" value={subject} onChange={setSubject} disabled={busy} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 170px' }}>
              <Input label="Start" type="datetime-local" value={start} onChange={setStart} disabled={busy} />
            </div>
            <div style={{ flex: '1 1 170px' }}>
              <Input label="End" type="datetime-local" value={end} onChange={setEnd} disabled={busy} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              {/* Editable and NOT defaulted: a timezone the app filled in is a
                  timezone nobody checked, and an hour out is worse than blank.
                  Empty means "my mailbox's own", which is stated here. */}
              <Input label="Timezone" value={tz} onChange={setTz} disabled={busy}
                     placeholder="America/Chicago" />
            </div>
          </div>
          <Input label="Location" value={where} onChange={setWhere} disabled={busy} />
          <Input label="Attendees (comma separated)" value={attendees} onChange={setAttendees} disabled={busy} />
          {!tz && (
            <div style={{ fontSize: 11, color: B.textMd }}>
              No timezone set — this will be created in your mailbox&apos;s own timezone.
            </div>
          )}
        </>
      )}

      {draft.kind === 'mailFiling' && (
        <>
          {/* THE TWO NUMBERS A PERSON CHECKS, above the list rather than
              inside it: how much mail moves, and how much of their folder
              structure this invents. */}
          <div style={{ fontSize: font.sm, color: B.text }}>
            <strong>{kept.length}</strong>
            {` of ${rows.length} message${rows.length === 1 ? '' : 's'} → `}
            <strong>{new Set(kept.map(r => r.folder)).size}</strong>
            {` folder${new Set(kept.map(r => r.folder)).size === 1 ? '' : 's'}`}
          </div>

          {newFolders.length > 0 && (
            <div style={{
              display: 'flex', gap: 7, alignItems: 'flex-start',
              fontSize: font.xs, color: B.amber, background: `${B.amber}12`,
              border: `1px solid ${B.amber}2e`, borderRadius: radius.lg, padding: '7px 10px',
            }}>
              <span style={{ display: 'flex', marginTop: 1 }}><Icon name="folder" size={13} /></span>
              <span>
                {newFolders.length === 1
                  ? 'This creates a folder you do not have yet: '
                  : `This creates ${newFolders.length} folders you do not have yet: `}
                <strong>{newFolders.join(', ')}</strong>
                {/* Never understate it: a destination two levels down creates
                    its parent as well, and the apply reports what it really
                    made. */}
                . Any missing parent folder is created too.
              </span>
            </div>
          )}

          {/* GROUPED BY DESTINATION: a plan is checked folder by folder
              ("is all of this La Presa?"), and changing a group's folder
              moves every message in it. */}
          <FilingGroups rows={rows} setRows={setRows} busy={busy}
                        existingFolders={payload.existingFolders || []} />
          <LeftOutList items={payload.leftOut || []} />
          {payload.foldersTruncated && (
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              You have more folders than Harvi could read, so it may not have seen the one you
              would have picked. Check the destinations.
            </div>
          )}
        </>
      )}

      {draft.kind === 'workbook' && (() => {
        const sheets = payload.sheets || [];
        const sheet = sheets[Math.min(sheetIdx, sheets.length - 1)] || { rows: [] };
        const rowsShown = sheet.rows.slice(0, PREVIEW_ROWS);
        const width = Math.min(PREVIEW_COLS, Math.max(0, ...rowsShown.map(r => r.length)));
        return (
          <>
            <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text }}>{payload.title}</div>
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              {sheets.length} sheet{sheets.length === 1 ? '' : 's'} · {payload.formulaCount || 0} formula
              {payload.formulaCount === 1 ? '' : 's'} · a README with the sources is added
            </div>
            {sheets.length > 1 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {sheets.map((sh, i) => (
                  <button key={sh.name} type="button" onClick={() => setSheetIdx(i)} style={{
                    fontSize: font.xs, padding: '3px 10px', borderRadius: radius.pill, cursor: 'pointer',
                    border: `1px solid ${i === sheetIdx ? B.navy : B.border}`,
                    background: i === sheetIdx ? B.navy : B.surface,
                    color: i === sheetIdx ? '#fff' : B.textMd,
                  }}>{sh.name}</button>
                ))}
              </div>
            )}
            <div style={{ overflowX: 'auto', border: `1px solid ${B.divider}`, borderRadius: radius.lg }}>
              <table style={{ borderCollapse: 'collapse', fontSize: font.xs, width: '100%' }}>
                <tbody>
                  {rowsShown.map((row, r) => (
                    <tr key={r} style={{ background: r < (sheet.headerRows || 0) ? B.surfaceMuted : 'transparent' }}>
                      {Array.from({ length: width }, (_, c) => {
                        const cell = row[c];
                        const isF = !!(cell && typeof cell === 'object' && cell.f);
                        return (
                          <td key={c} style={{
                            padding: '3px 8px', borderBottom: `1px solid ${B.divider}`, whiteSpace: 'nowrap',
                            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
                            color: isF ? B.navy : B.text, fontFamily: isF ? 'monospace' : undefined,
                            fontWeight: r < (sheet.headerRows || 0) ? weight.semibold : undefined,
                          }}>{cellText(cell)}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(sheet.rows.length > PREVIEW_ROWS || sheet.rows.some(r => r.length > PREVIEW_COLS)) && (
              <div style={{ fontSize: font.xs, color: B.textLt }}>
                Showing the top-left corner — {sheet.rows.length} rows in this sheet.
              </div>
            )}
            {(payload.sources || []).length > 0 && (
              <div style={{ fontSize: font.xs, color: B.textMd }}>
                Sources:
                <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                  {payload.sources.map((src, i) => <li key={i}>{src}</li>)}
                </ul>
              </div>
            )}
          </>
        );
      })()}

      {draft.kind === 'document' && (
        <>
          <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text }}>{payload.title}</div>
          <div style={{ fontSize: font.xs, color: B.textMd }}>
            About {Number(payload.wordCount || 0).toLocaleString('en-US')} words
            {payload.tableCount ? ` · ${payload.tableCount} table${payload.tableCount === 1 ? '' : 's'}` : ''}
            {' '}· a Sources section is added
          </div>
          {(payload.logos || []).length > 0 && (
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              Logos: {payload.logos.map(l => `${l.label || l.key} (${l.position})`).join(' · ')}
            </div>
          )}
          {payload.summary && (
            <div style={{ fontSize: font.sm, color: B.text, whiteSpace: 'pre-wrap' }}>{payload.summary}</div>
          )}
          {(payload.outline || []).length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: font.xs, color: B.textMd }}>
              {payload.outline.map((h, i) => (
                <li key={i} style={{ marginLeft: h.level > 1 ? 12 : 0, fontWeight: h.level === 1 ? weight.semibold : undefined }}>{h.text}</li>
              ))}
            </ul>
          )}
          {/* The raw Markdown, bounded — a glance at the prose, not the file. */}
          <div style={{
            maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: font.xs, color: B.textMd,
            border: `1px solid ${B.divider}`, borderRadius: radius.lg, padding: `${space.sm}px ${space.md}px`,
            background: B.surfaceMuted,
          }}>{String(payload.body || '').slice(0, 4000)}</div>
          {(payload.sources || []).length > 0 && (
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              Sources:
              <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                {payload.sources.map((src, i) => <li key={i}>{src}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {draft.kind === 'mailAttachment' && (
        // WHICH FILE, AND HOW BIG — the two facts a person checks before
        // pressing Save. There is nothing else on this card to edit: the
        // message, the attachment and the target are all fixed at propose
        // time (see normalizeAttachmentDraft), and the target is already
        // shown above in the header's "On {targetLabel}" line every kind
        // shares. A wrong target is corrected by discarding and asking Harvi
        // again, the same recovery path a wrong note/task target already has.
        <div style={{
          display: 'flex', gap: space.sm, alignItems: 'center',
          padding: `${space.sm}px ${space.md}px`,
          border: `1px solid ${B.divider}`, borderRadius: radius.lg,
          background: B.surfaceMuted,
        }}>
          <span style={{ display: 'flex', color: B.textMd, flexShrink: 0 }}>
            <Icon name="file" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: font.sm, fontWeight: weight.semibold, color: B.text,
              wordBreak: 'break-word',
            }}>{payload.fileName || 'This attachment'}</div>
            {fmtBytes(payload.sizeBytes) && (
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 1 }}>
                {fmtBytes(payload.sizeBytes)}
              </div>
            )}
          </div>
        </div>
      )}

      {applied && (
        <div style={{
          display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: font.sm, color: B.green,
          background: `${B.green}12`, border: `1px solid ${B.green}2e`,
          borderRadius: radius.lg, padding: '8px 10px',
        }}>
          <span style={{ display: 'flex', marginTop: 1 }}><Icon name="success" size={13} /></span>
          <span style={{ minWidth: 0 }}>
            {applied.message}
            {applied.webLink && (
              <>
                {' '}
                <a href={applied.webLink} target="_blank" rel="noreferrer" style={{ color: B.navy }}>Open it</a>
              </>
            )}
            {/* WHAT DID NOT MOVE IS NAMED. A plan is partially appliable, so a
                message saying "12 filed" over a plan of 14 has to account for
                the other two — they are still in the Inbox, and the person
                needs to know which. */}
            {(applied.skipped || []).length > 0 && (
              <span style={{ display: 'block', marginTop: 4, color: B.amber }}>
                Left in your Inbox:
                <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                  {applied.skipped.map((sk, i) => (
                    <li key={i} style={{ fontSize: font.xs }}>
                      <strong>{sk.subject}</strong>{sk.reason ? ` — ${sk.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </span>
            )}
          </span>
        </div>
      )}

      {err && (
        <div style={{
          display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: font.sm, color: B.red,
          background: `${B.red}0f`, border: `1px solid ${B.red}33`,
          borderRadius: radius.lg, padding: '8px 10px',
        }}>
          <span style={{ display: 'flex', marginTop: 1 }}><Icon name="warning" size={13} /></span>{err}
        </div>
      )}

      {dupes && (
        <DuplicateTaskDialog
          newTitle={title}
          similarTasks={dupes}
          busy={busy}
          onCancel={() => setDupes(null)}
          onConfirm={() => save(true)}
        />
      )}

      <div style={{
        display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap',
        paddingTop: space.md, borderTop: `1px solid ${B.divider}`,
      }}>
        <Btn size="sm" onClick={() => save(false)} loading={busy}
             disabled={busy || !hasRequired}>
          {copy.save}
        </Btn>
        {draft.kind === 'document' && (
          <Btn size="sm" variant="secondary" onClick={() => save(false, { format: 'pdf' })} disabled={busy}>
            Download PDF
          </Btn>
        )}
        {isFileKind && (
          <Btn size="sm" variant="secondary" onClick={() => setSavingToDeal(true)} disabled={busy}>
            Save to deal
          </Btn>
        )}
        <Btn size="sm" variant="ghost" onClick={discard} disabled={busy}>Discard</Btn>
        <span style={{ fontSize: font.xs, color: B.textLt, marginLeft: 'auto' }}>
          You are saving this, not Harvi.
        </span>
      </div>
      </div>
    </div>
  );
};

export default AssistantDraftCard;

// ── Mail filing, grouped by destination ─────────────────────────────────────

const isArchive = f => /^archive$/i.test(String(f || '').trim());

function FilingTag({ tone, children }) {
  const color = tone === 'new' ? B.amber : tone === 'fix' ? B.green : tone === 'deal' ? B.navy : B.textMd;
  return (
    <span style={{
      fontSize: 10, fontWeight: weight.bold, letterSpacing: 0.3, textTransform: 'uppercase',
      color, background: `${color}14`, borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export function FilingGroups({ rows, setRows, busy, existingFolders }) {
  const have = useMemo(() => new Set((existingFolders || []).map(f => String(f).toLowerCase())), [existingFolders]);
  // Groups in first-seen order, the archive last (it is the bulk and the least
  // interesting to check).
  const groups = useMemo(() => {
    const m = new Map();
    rows.forEach((r, i) => {
      const key = String(r.folder || '').trim().toLowerCase();
      if (!m.has(key)) m.set(key, { key, folder: r.folder || '', idx: [] });
      m.get(key).idx.push(i);
    });
    const list = [...m.values()];
    return [...list.filter(g => !isArchive(g.folder)), ...list.filter(g => isArchive(g.folder))];
  }, [rows]);
  // What the picker offers: every folder the mailbox has, plus the new folders
  // this plan is already creating — so a message can join the "La Presa" group
  // even though that folder does not exist yet.
  const options = useMemo(() => {
    const out = [...(existingFolders || [])];
    const seen = new Set(out.map(f => String(f).toLowerCase()));
    for (const r of rows) {
      const f = String(r.folder || '').trim();
      if (f && !seen.has(f.toLowerCase())) { seen.add(f.toLowerCase()); out.push(f); }
    }
    return out;
  }, [existingFolders, rows]);
  const [closed, setClosed] = useState({});
  const isClosed = g => (closed[g.key] ?? (isArchive(g.folder) && g.idx.length > 5));

  const setFolderFor = (idx, v) => setRows(rs => rs.map((x, j) => (idx.includes(j) ? { ...x, folder: v } : x)));
  const setKeepFor = (idx, v) => setRows(rs => rs.map((x, j) => (idx.includes(j) ? { ...x, keep: v } : x)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      {groups.map(g => {
        const members = g.idx.map(i => rows[i]);
        const keptN = members.filter(r => r.keep).length;
        const isNew = !!g.folder && !have.has(String(g.folder).toLowerCase());
        const adj = members.find(r => r.adjusted)?.adjusted;
        const open = !isClosed(g);
        return (
          <div key={g.key || '(none)'} style={{ border: `1px solid ${B.divider}`, borderRadius: radius.lg, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, padding: `${space.sm}px ${space.md}px`, background: B.surfaceMuted, flexWrap: 'wrap' }}>
              <input type="checkbox" disabled={busy}
                checked={keptN === members.length}
                ref={el => { if (el) el.indeterminate = keptN > 0 && keptN < members.length; }}
                aria-label={`File every message going to ${g.folder || 'this folder'}`}
                onChange={e => setKeepFor(g.idx, e.target.checked)} />
              <button type="button" onClick={() => setClosed(c => ({ ...c, [g.key]: open }))}
                aria-expanded={open}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: B.textMd, display: 'flex',
                         transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
                <Icon name="chevronRight" size={14} />
              </button>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: font.sm, fontWeight: weight.bold, color: B.text }}>{g.folder || '(no folder)'}</span>
                  {isNew && <FilingTag tone="new">New folder</FilingTag>}
                  {adj?.rule === 'deal' && <FilingTag tone="deal">Deal match</FilingTag>}
                  {adj?.rule === 'thread' && <FilingTag>Thread · no deal</FilingTag>}
                  {adj && <FilingTag tone="fix">was {adj.was}</FilingTag>}
                </div>
                {adj && (
                  <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 1 }}>
                    {adj.rule === 'deal'
                      ? `The subject names the deal ${adj.dealName}.`
                      : `${adj.threadSize} messages in one conversation, kept together.`}
                  </div>
                )}
              </div>
              <div style={{ flex: '0 0 190px' }}>
                <FolderCombo value={g.folder} disabled={busy} folders={options}
                             label="Folder for every message in this group" showNew={false}
                             onChange={v => setFolderFor(g.idx, v)} />
              </div>
              <span style={{ fontSize: font.xs, color: B.textMd, minWidth: 24, textAlign: 'right' }}>{keptN}/{members.length}</span>
            </div>
            {open && g.idx.map((i, n) => {
              const r = rows[i];
              return (
                <div key={r.messageId} style={{
                  display: 'flex', gap: space.sm, alignItems: 'flex-start',
                  padding: `${space.sm}px ${space.md}px`,
                  borderTop: `1px solid ${B.divider}`,
                  opacity: r.keep ? 1 : 0.55,
                }}>
                  <input type="checkbox" checked={r.keep} disabled={busy}
                    aria-label={`File "${r.subject || 'this message'}"`}
                    onChange={e => setKeepFor([i], e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: font.sm, color: B.text, fontWeight: weight.semibold, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.subject || '(no subject)'}
                    </div>
                    <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 1 }}>
                      {[r.from, r.receivedAt ? formatInstant(r.receivedAt, { month: 'short', day: 'numeric', year: 'numeric' }) : null].filter(Boolean).join(' · ')}
                    </div>
                    {r.reason && !r.adjusted && (
                      <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2, fontStyle: 'italic' }}>{r.reason}</div>
                    )}
                  </div>
                  {/* Moving ONE message out of its group. */}
                  <div style={{ flex: '0 0 170px' }}>
                    <FolderCombo value={r.folder || ''} disabled={busy || !r.keep} folders={options} existing={existingFolders}
                                 label="Folder for this message"
                                 onChange={v => setFolderFor([i], v)} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── What the plan leaves in the Inbox ───────────────────────────────────────
// A batch of twenty that files eleven must say where the other nine went:
// each one, with Harvi's reason for leaving it. Nothing here moves.
export function LeftOutList({ items }) {
  const [open, setOpen] = useState(true);
  if (!items || !items.length) return null;
  return (
    <div style={{ border: `1px solid ${B.divider}`, borderRadius: radius.lg, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: space.sm, width: '100%', padding: `${space.sm}px ${space.md}px`,
          background: B.surfaceMuted, border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <span style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text }}>Staying in your Inbox</span>
        <span style={{ fontSize: font.xs, color: B.textMd }}>{items.length} message{items.length === 1 ? '' : 's'} — not in this plan</span>
      </button>
      {open && items.map(r => (
        <div key={r.messageId} style={{ padding: `${space.sm}px ${space.md}px`, borderTop: `1px solid ${B.divider}` }}>
          <div style={{ fontSize: font.sm, color: B.text, fontWeight: weight.semibold, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.subject || '(no subject)'}
          </div>
          <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 1 }}>
            {[r.from, r.receivedAt ? formatInstant(r.receivedAt, { month: 'short', day: 'numeric', year: 'numeric' }) : null].filter(Boolean).join(' · ')}
          </div>
          {r.reason && <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2, fontStyle: 'italic' }}>{r.reason}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Folder picker ───────────────────────────────────────────────────────────
// Type to filter, or open the list to browse every folder the mailbox has
// (the plan's snapshot of the live tree). A name that matches nothing is still
// accepted — that is how a new folder is asked for — and is flagged as new.

// The list is positioned FIXED against the input rather than absolutely inside
// it: the draft card clips its overflow (rounded corners), so an absolute list
// on the last rows of a plan would be cut off. Scrolling closes it.
export function FolderCombo({ value, onChange, folders = [], existing, disabled, label, showNew = true }) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  // What is being typed. It is NOT handed to onChange per keystroke: the plan
  // groups rows by folder, so every keystroke re-grouped the row, remounted this
  // input and dropped focus — typing "Black Lumber" left "Bla" and no list.
  // The choice is committed on a pick, Enter, Tab or leaving the field.
  const [draft, setDraft] = useState(null);   // null = not editing
  const [active, setActive] = useState(-1);
  const wrap = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const commit = (v) => {
    setDraft(null); setOpen(false); setActive(-1);
    const next = String(v ?? '').trim();
    if (next && next !== String(value || '')) onChange(next);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (!open) return undefined;
    if (wrap.current) setRect(wrap.current.getBoundingClientRect());
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) {
        if (draftRef.current != null) commitRef.current(draftRef.current); else setOpen(false);
      }
    };
    const onScroll = (e) => { if (!(e.target && e.target.getAttribute && e.target.getAttribute('role') === 'listbox')) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  // Archive first, then alphabetical by full path.
  const sorted = useMemo(() => {
    const list = [...new Set((folders || []).map(String))];
    return list.sort((a, b) => (isArchive(b) - isArchive(a)) || a.localeCompare(b));
  }, [folders]);
  const text = draft ?? String(value || '');
  const q = (draft ?? '').trim().toLowerCase();
  // Typing matches anywhere in the path; a folder whose OWN name starts with
  // what was typed ("Bla" → Deals/Black Lumber) comes first.
  const shown = useMemo(() => {
    if (!q) return sorted.slice(0, 200);
    const leaf = f => f.split('/').pop().toLowerCase();
    const rank = f => (leaf(f).startsWith(q) ? 0 : f.toLowerCase().startsWith(q) ? 1 : leaf(f).includes(q) ? 2 : 3);
    return sorted.filter(f => f.toLowerCase().includes(q))
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).slice(0, 200);
  }, [sorted, q]);
  const exactHit = q && sorted.some(f => f.toLowerCase() === q);
  const offerCreate = !!q && !exactHit;
  const createIndex = shown.length;   // the "Create …" row sits after the matches
  const exists = (existing || sorted).some(f => String(f).toLowerCase() === text.trim().toLowerCase());

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'stretch', border: `1px solid ${B.border}`, borderRadius: radius.md || 8,
                    background: disabled ? B.surfaceMuted : B.surface, overflow: 'hidden' }}>
        <input
          value={text} disabled={disabled} aria-label={label}
          role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          placeholder="Type to find a folder"
          onChange={e => { setDraft(e.target.value); setOpen(true); setActive(e.target.value.trim() ? 0 : -1); }}
          onFocus={e => { e.target.select(); setOpen(true); }}
          onKeyDown={e => {
            const max = shown.length - (offerCreate ? 0 : 1);
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, max)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
            else if (e.key === 'Enter') {
              e.preventDefault();
              if (open && active >= 0 && active < shown.length) commit(shown[active]);
              else if (draft != null) commit(draft);
              else setOpen(false);
            }
            else if (e.key === 'Escape') { setDraft(null); setOpen(false); setActive(-1); }
            else if (e.key === 'Tab') { if (draft != null) commit(open && active >= 0 && active < shown.length ? shown[active] : draft); else setOpen(false); }
          }}
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', padding: '6px 8px',
                   fontSize: font.sm, color: B.text, fontFamily: 'inherit' }} />
        <button type="button" disabled={disabled} tabIndex={-1} aria-label="Show folders"
          onClick={() => { setDraft(null); setActive(-1); setOpen(o => !o); }}
          style={{ border: 'none', borderLeft: `1px solid ${B.divider}`, background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
                   color: B.textMd, padding: '0 6px', display: 'flex', alignItems: 'center' }}>
          <Icon name="chevronDown" size={13} />
        </button>
      </div>
      {showNew && draft == null && value && !exists && (
        <span style={{ fontSize: 10, color: B.amber, fontWeight: weight.semibold }}>New folder</span>
      )}
      {open && !disabled && rect && (
        <div id={listId} role="listbox" style={{
          position: 'fixed', zIndex: 1100, left: rect.left, width: Math.max(rect.width, 260),
          ...(window.innerHeight - rect.bottom < 270 && rect.top > 270
            ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }),
          maxHeight: 260, overflowY: 'auto', background: B.surface, border: `1px solid ${B.border}`,
                                     borderRadius: radius.lg, boxShadow: B.shadowLg || '0 8px 24px rgba(26,56,68,.16)' }}>
          {shown.length === 0 && !offerCreate && (
            <div style={{ padding: '8px 10px', fontSize: font.xs, color: B.textMd }}>No folders were read from this mailbox.</div>
          )}
          {shown.map((f, i) => {
            const sel = String(value || '').toLowerCase() === f.toLowerCase();
            const depth = f.split('/').length - 1;
            return (
              <div key={f} role="option" aria-selected={sel}
                onMouseDown={e => { e.preventDefault(); commit(f); }}
                onMouseEnter={() => setActive(i)}
                style={{ padding: '6px 10px', paddingLeft: 10 + (q ? 0 : depth * 12), fontSize: font.sm, cursor: 'pointer',
                         color: B.text, background: i === active ? B.surfaceMuted : 'transparent', fontWeight: sel ? weight.semibold : 400,
                         whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                title={f}>
                {q || depth === 0 ? f : f.split('/').pop()}
              </div>
            );
          })}
          {offerCreate && (
            <div role="option" aria-selected={active === createIndex}
              onMouseDown={e => { e.preventDefault(); commit(draft); }}
              onMouseEnter={() => setActive(createIndex)}
              style={{ padding: '6px 10px', fontSize: font.sm, cursor: 'pointer', color: B.amber,
                       borderTop: shown.length ? `1px solid ${B.divider}` : 'none',
                       background: active === createIndex ? B.surfaceMuted : 'transparent',
                       whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              + Create new folder "<strong>{draft.trim()}</strong>"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
